import { motion } from "framer-motion";
import { Instagram, Linkedin } from "lucide-react";
import { useTranslation } from "react-i18next";

export interface Founder {
  role: string;
  name: string;
  initials: string;
  bio: string;
  quote: string;
  tags: string[];
  photo?: string;
  linkedin?: string;
  instagram?: string;
  hue: number;
}

function FounderAvatar({ founder }: { founder: Founder }) {
  return (
    <div
      className="mx-auto w-full max-w-[280px] aspect-square sm:max-w-none sm:aspect-auto sm:w-[220px] sm:h-[220px] md:w-[280px] md:h-[280px]"
      style={{
        borderRadius: "50%",
        overflow: "hidden",
        border: "1px solid var(--dyna-border)",
        position: "relative",
        background: `linear-gradient(145deg, hsl(${founder.hue},48%,22%) 0%, hsl(${founder.hue - 15},42%,12%) 100%)`,
        boxShadow: `0 12px 40px -8px hsla(${founder.hue},45%,40%,0.3)`,
        flexShrink: 0,
      }}
    >
      {founder.photo ? (
        <img src={founder.photo} alt={founder.name} style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "top" }} />
      ) : (
        <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <span
            className="text-[66px] md:text-[84px]"
            style={{
              fontFamily: "Coolvetica, system-ui, sans-serif",
              fontWeight: 700,
              letterSpacing: "0.06em",
              color: `hsla(${founder.hue},60%,80%,0.55)`,
            }}
          >
            {founder.initials}
          </span>
        </div>
      )}
    </div>
  );
}

export function FounderCard({ founder }: { founder: Founder; reverse?: boolean }) {
  return (
    <motion.div
      className="dyna-about__person-card"
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
    >
      <div className="dyna-about__person-head" style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        gap: 12,
      }}>
        <FounderAvatar founder={founder} />

        <span className="dyna-about__person-role" style={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase",
          color: "var(--lavender-400)",
        }}>
          {founder.role}
        </span>

        <h3 className="dyna-about__person-name" style={{
          fontFamily: "Coolvetica, system-ui, sans-serif",
          fontSize: "clamp(24px, 2.5vw, 32px)",
          fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase",
          color: "var(--dyna-fg)", margin: 0,
          lineHeight: 1.05,
        }}>
          {founder.name}
        </h3>
      </div>

      <p className="dyna-about__person-bio" style={{
        fontSize: 15, lineHeight: 1.7,
        color: "var(--dyna-fg-muted)", margin: "20px 0 16px",
        fontFamily: "Geist, system-ui, sans-serif", fontWeight: 300,
        textAlign: "center",
      }}>
        {founder.bio}
      </p>

      <blockquote className="dyna-about__person-quote" style={{
        borderLeft: "2px solid var(--lavender-600)",
        paddingLeft: 16, margin: "0 0 20px",
        fontFamily: "'Instrument Serif', serif",
        fontStyle: "italic", fontSize: 17,
        color: "var(--lavender-300)", lineHeight: 1.55,
        textAlign: "center",
      }}>
        &ldquo;{founder.quote}&rdquo;
      </blockquote>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16, justifyContent: "center" }}>
        {founder.tags.map(t => (
          <span key={t} style={{
            fontSize: 11, fontWeight: 500, letterSpacing: "0.1em", textTransform: "uppercase",
            fontFamily: "Geist, system-ui, sans-serif",
            color: "var(--lavender-400)", border: "1px solid var(--dyna-border)",
            borderRadius: 999, padding: "4px 12px",
          }}>
            {t}
          </span>
        ))}
      </div>

      {(founder.linkedin || founder.instagram) && (
        <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
          {founder.linkedin && (
            <a href={founder.linkedin} target="_blank" rel="noopener noreferrer"
              style={{
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                width: 36, height: 36, borderRadius: "50%",
                border: "1px solid var(--dyna-border)",
                color: "var(--lavender-400)", transition: "all 200ms",
              }}
              onMouseEnter={e => {
                (e.currentTarget as HTMLElement).style.background = "var(--lavender-600)";
                (e.currentTarget as HTMLElement).style.borderColor = "var(--lavender-600)";
                (e.currentTarget as HTMLElement).style.color = "#fff";
              }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLElement).style.background = "transparent";
                (e.currentTarget as HTMLElement).style.borderColor = "var(--dyna-border)";
                (e.currentTarget as HTMLElement).style.color = "var(--lavender-400)";
              }}
            >
              <Linkedin size={15} />
            </a>
          )}
          {founder.instagram && (
            <a href={founder.instagram} target="_blank" rel="noopener noreferrer"
              style={{
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                width: 36, height: 36, borderRadius: "50%",
                border: "1px solid var(--dyna-border)",
                color: "var(--lavender-400)", transition: "all 200ms",
              }}
              onMouseEnter={e => {
                (e.currentTarget as HTMLElement).style.background = "var(--lavender-600)";
                (e.currentTarget as HTMLElement).style.borderColor = "var(--lavender-600)";
                (e.currentTarget as HTMLElement).style.color = "#fff";
              }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLElement).style.background = "transparent";
                (e.currentTarget as HTMLElement).style.borderColor = "var(--dyna-border)";
                (e.currentTarget as HTMLElement).style.color = "var(--lavender-400)";
              }}
            >
              <Instagram size={15} />
            </a>
          )}
        </div>
      )}
    </motion.div>
  );
}

export default function AboutSection() {
  const { t } = useTranslation();

  const founders: Founder[] = [
    {
      role: t("dyna.about.role1"),
      name: t("dyna.about.name1"),
      initials: "PH",
      hue: 270,
      bio: t("dyna.about.bio1"),
      quote: t("dyna.about.quote1"),
      tags: [t("dyna.about.tag1a"), t("dyna.about.tag1b"), t("dyna.about.tag1c")],
      photo: "/team/pinch.webp",
      linkedin: "",
      instagram: "",
    },
    {
      role: t("dyna.about.role2"),
      name: t("dyna.about.name2"),
      initials: "ER",
      hue: 255,
      bio: t("dyna.about.bio2"),
      quote: t("dyna.about.quote2"),
      tags: [t("dyna.about.tag2a"), t("dyna.about.tag2b"), t("dyna.about.tag2c")],
      photo: "/team/ezra.webp",
      linkedin: "https://www.linkedin.com/in/ezra-rothchild-624b9a203/",
      instagram: "https://www.instagram.com/ezra_rothchild/",
    },
  ];

  return (
    <section id="about" style={{ padding: "var(--s-lg) var(--site-px)", maxWidth: "var(--site-max)", margin: "0 auto" }}>
      {/* Better together */}
      <motion.div
        initial={{ opacity: 0, y: 32 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        style={{ marginTop: "var(--s-lg)" }}
      >
        <div className="dyna-about__better" style={{
          borderRadius: 24,
          overflow: "hidden",
          border: "1px solid var(--dyna-border)",
          background: "radial-gradient(ellipse at 50% 0%, var(--color-soft) 0%, transparent 65%), linear-gradient(180deg, var(--dyna-bg-raised), var(--dyna-bg-elevated))",
          boxShadow: "var(--glow-nova)",
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
        }}>
          {/* Combined photo placeholder — photos take equal width and a
              portrait-golden (1 : 1.618) aspect, so they fill the left
              column with consistent proportions and drive the card height. */}
          <div style={{
            position: "relative",
            background: "linear-gradient(135deg, var(--dyna-bg) 0%, var(--dyna-bg-raised) 50%, var(--dyna-bg-elevated) 100%)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: "clamp(28px, 4vw, 56px)",
            overflow: "hidden",
          }}>
            <div style={{
              display: "flex", gap: 16, width: "100%",
            }}>
              {founders.map(f => (
                <div key={f.name} style={{
                  flex: "1 1 0",
                  aspectRatio: "1 / 1.4",
                  borderRadius: 18,
                  background: `linear-gradient(145deg, hsl(${f.hue},48%,22%) 0%, hsl(${f.hue - 15},42%,12%) 100%)`,
                  border: "1px solid var(--dyna-border)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  boxShadow: `0 12px 40px -8px hsla(${f.hue},45%,40%,0.3)`,
                  overflow: "hidden",
                }}>
                  {f.photo ? (
                    <img src={f.photo} alt={f.name} style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "top" }} />
                  ) : (
                    <span style={{
                      fontFamily: "Coolvetica, system-ui, sans-serif",
                      fontSize: "clamp(36px, 5vw, 56px)", fontWeight: 700,
                      color: `hsla(${f.hue},60%,80%,0.4)`,
                      letterSpacing: "0.06em",
                    }}>
                      {f.initials}
                    </span>
                  )}
                </div>
              ))}
            </div>
            <div style={{
              position: "absolute", inset: 0,
              background: "linear-gradient(to right, transparent 60%, rgba(10,8,15,0.6))",
              pointerEvents: "none",
            }} />
          </div>

          {/* Copy */}
          <div style={{ padding: "clamp(32px, 5vw, 56px)", display: "flex", flexDirection: "column", justifyContent: "center" }}>
            <span style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase",
              color: "var(--lavender-500)", marginBottom: 16, display: "block",
            }}>
              {t("dyna.about.better_eyebrow")}
            </span>
            <h3 style={{
              fontFamily: "Coolvetica, system-ui, sans-serif",
              fontSize: "clamp(28px, 3vw, 40px)",
              fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase",
              color: "var(--dyna-fg)", margin: "0 0 20px", lineHeight: 1.05,
            }}>
              {t("dyna.about.better_heading")}<br />
              <span style={{
                fontFamily: "'Instrument Serif', serif",
                fontStyle: "italic", fontWeight: 400, textTransform: "lowercase",
                color: "var(--lavender-300)",
              }}>
                {t("dyna.about.better_heading_em")}
              </span>
            </h3>
            <p style={{
              fontSize: 15, lineHeight: 1.7, color: "var(--dyna-fg-muted)",
              fontFamily: "Geist, system-ui, sans-serif", fontWeight: 300,
              margin: "0 0 28px", maxWidth: 400,
            }}>
              {t("dyna.about.better_body")}
            </p>
            <a
              href="/#contact"
              style={{
                display: "inline-flex", alignItems: "center", gap: 8,
                fontSize: 13, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase",
                fontFamily: "Geist, system-ui, sans-serif",
                color: "var(--lavender-300)", textDecoration: "none",
                border: "1px solid var(--dyna-border-strong)",
                borderRadius: 999, padding: "10px 22px",
                transition: "all 200ms", width: "fit-content",
              }}
              onMouseEnter={e => {
                (e.currentTarget as HTMLElement).style.background = "var(--lavender-600)";
                (e.currentTarget as HTMLElement).style.color = "#fff";
                (e.currentTarget as HTMLElement).style.borderColor = "var(--lavender-600)";
              }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLElement).style.background = "transparent";
                (e.currentTarget as HTMLElement).style.color = "var(--lavender-300)";
                (e.currentTarget as HTMLElement).style.borderColor = "var(--dyna-border-strong)";
              }}
            >
              {t("dyna.about.better_cta")} <span style={{ marginLeft: 2 }}>→</span>
            </a>
          </div>
        </div>
      </motion.div>
    </section>
  );
}
